import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Modal from "@commons/Modal";
import TextInput from "@commons/Input/Text";
import { usePage } from "@hooks/usePage";
import SelectInput from "@commons/Input/Select";
import UploadInput from "@commons/Input/Upload";
import Heading from "@commons/Typography/Heading";
import {
  GENDER_OPTIONS,
  GENDER_VALUES,
  SITUATION_OPTIONS,
  SITUATION_VALUES,
} from "@typings/entities/individuals";
import { config } from "@/config";
import {
  validateCoordinates,
  validateLatitude,
  validateLongitude,
  validatePositiveInteger,
  validatePositiveNumber,
} from "@utils/formatters";
import type { TaxonomicGroup } from "@typings/entities/taxonomicGroup";
import ComboBox from "@commons/Input/ComboBox";
import { searchTaxonomic } from "@services/taxonomicGroups/search";
import { toast } from "react-toastify";
import { AppError } from "@utils/errors/appError";
import { createIndividual } from "@services/individuals/create";
import type { CreateIndividualDTO } from "@services/individuals/create/type";
import { createAttachmentsIndividual } from "@services/individuals/createAttachments";
import { useEffect } from "react";
import Map from "@commons/Map";
import PublicIcon from "@mui/icons-material/Public";
import Text from "@commons/Typography/Text";
import { globalMutate } from "@utils/swr";
import { useModalOpenWithCoordinates } from "@hooks/useModalOpenWithCoordinates";

const individualSchema = z.object({
  clinicalRecord: z.string().min(1, "Nº ficha clínica é obrigatório"),
  entranceDate: z.string().min(1, "Data de entrada é obrigatória"),
  commonName: z.string().min(1, "Nome comum é obrigatório"),
  ufuName: z.string().min(1, "Nome UFU é obrigatório"),
  species: z.object({
    label: z.string().min(1, "Espécie Indefinida"),
    value: z.string().min(1, "Espécie Indefinida"),
  }),
  situation: z.enum(SITUATION_VALUES, {
    error: "Situação deve ser um valor válido",
  }),
  ageEstimateYear: z
    .string()
    .refine(validatePositiveInteger(), {
      message: "Idade deve ser um número inteiro positivo",
    })
    .optional(),
  weightKg: z
    .string()
    .refine(validatePositiveNumber(), {
      message: "Peso deve ser maior que 0",
    })
    .optional(),
  gender: z.enum(GENDER_VALUES, {
    error: "Gênero deve ser um valor válido",
  }),
  markingType: z.string().optional(),
  marking: z.string().optional(),
  latitude: z
    .string({ error: "Latitude é obrigatória" })
    .refine(validateLatitude(), {
      message: "Latitude deve estar entre -90 e 90",
    }),
  longitude: z
    .string({ error: "Longitude é obrigatória" })
    .refine(validateLongitude(), {
      message: "Longitude deve estar entre -180 e 180",
    }),
  observations: z.string().optional(),
  attachments: z
    .array(z.instanceof(File))
    .max(
      config.uploadArchive.maxFiles,
      `Você pode enviar no máximo ${config.uploadArchive.maxFiles} arquivos.`,
    )
    .optional(),
});

type IndividualFormData = z.infer<typeof individualSchema>;

function ModalNewIndividual() {
  const { isModalOpen, closeModal, setIsLoading, setDataModal } = usePage();
  const open = isModalOpen("newIndividual");

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<IndividualFormData>({
    resolver: zodResolver(individualSchema),
    defaultValues: {
      clinicalRecord: "",
      entranceDate: "",
      commonName: "",
      ufuName: "",
      species: { label: "", value: "" },
      situation: undefined,
      ageEstimateYear: undefined,
      weightKg: undefined,
      gender: undefined,
      markingType: "",
      marking: undefined,
      latitude: undefined,
      longitude: undefined,
      observations: "",
      attachments: [],
    },
  });

  useModalOpenWithCoordinates<IndividualFormData>({ open, setValue });

  const markingType = watch("markingType");
  const isMarkingDisabled = !markingType || markingType.trim() === "";

  useEffect(() => {
    if (isMarkingDisabled) {
      setValue("marking", undefined);
    }
  }, [isMarkingDisabled, setValue]);

  const onSubmit = async (data: IndividualFormData) => {
    try {
      setIsLoading(true);

      const payload: Partial<CreateIndividualDTO> & {
        clinicalRecord: string;
        entranceDate: string;
        commonName: string;
        ufuName: string;
        taxonomicGroupId: string;
        situation: string;
        gender: string;
        captureLatitude: number;
        captureLongitude: number;
      } = {
        clinicalRecord: data.clinicalRecord,
        entranceDate: data.entranceDate,
        commonName: data.commonName,
        ufuName: data.ufuName,
        taxonomicGroupId: data.species?.value ?? "",
        situation: String(data.situation),
        gender: String(data.gender),
        captureLatitude: Number(data.latitude),
        captureLongitude: Number(data.longitude),
        captureWeightKg:
          data.weightKg != null ? Number(data.weightKg) : undefined,
        captureAgeYear:
          data.ageEstimateYear != null
            ? Number(data.ageEstimateYear)
            : undefined,
        captureObservations: data.observations || undefined,
      };

      if (data.markingType?.trim()) {
        payload.markingType = data.markingType.trim();
      }

      if (data.marking?.trim()) {
        payload.marking = data.marking.trim();
      }

      const individualCreated = await createIndividual(
        payload as CreateIndividualDTO,
      );

      if (data.attachments && data.attachments.length > 0) {
        await createAttachmentsIndividual({
          id: individualCreated.id,
          files: data.attachments || [],
        });
      }

      toast.success("Indivíduo criado com sucesso");

      globalMutate(["fetch-get-individuals", "fetch-individuals-geojson"]);
    } catch (error) {
      const isAppError = error instanceof AppError;
      const title = isAppError ? error.message : "Erro ao criar indivíduo";

      toast.error(title);
    } finally {
      setIsLoading(false);
      handleClose();
    }
  };

  const handleClose = () => {
    reset();
    setDataModal({});
    closeModal();
  };

  if (!open) return null;

  const latitude = watch("latitude");
  const longitude = watch("longitude");

  const { hasValidCoordinates, lat, lng } = validateCoordinates(
    latitude,
    longitude,
  );

  return (
    <Modal
      open={open}
      heading="Cadastro de Novo Indivíduo"
      onClose={handleClose}
      onConfirm={handleSubmit(onSubmit)}
      confirmLabel="Salvar"
      cancelLabel="Cancelar"
      className="max-w-4xl"
    >
      <form className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        <Controller
          name="clinicalRecord"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Nº Ficha Clínica"
              textSize="sm"
              fullWidth
              error={!!errors.clinicalRecord}
              helperText={errors.clinicalRecord?.message}
            />
          )}
        />

        <Controller
          name="entranceDate"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Data de Entrada"
              type="datetime-local"
              textSize="sm"
              fullWidth
              error={!!errors.entranceDate}
              helperText={errors.entranceDate?.message}
            />
          )}
        />

        <Controller
          name="commonName"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Nome Comum"
              textSize="sm"
              fullWidth
              error={!!errors.commonName}
              helperText={errors.commonName?.message}
            />
          )}
        />

        <Controller
          name="ufuName"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              label="Nome UFU"
              textSize="sm"
              fullWidth
              error={!!errors.ufuName}
              helperText={errors.ufuName?.message}
            />
          )}
        />

        <Controller
          name="species"
          control={control}
          render={({ field }) => (
            <ComboBox<TaxonomicGroup>
              value={field.value ?? null}
              onChange={(option) => field.onChange(option ?? null)}
              searchService={searchTaxonomic}
              label="Espécie"
              error={!!errors.species}
              helperText={errors.species?.message}
            />
          )}
        />

        <Controller
          name="situation"
          control={control}
          render={({ field }) => (
            <SelectInput
              {...field}
              value={field.value || ""}
              label="Situação"
              size="small"
              fullWidth
              error={!!errors.situation}
              helperText={errors.situation?.message}
              options={SITUATION_OPTIONS}
            />
          )}
        />

        <Controller
          name="gender"
          control={control}
          render={({ field }) => (
            <SelectInput
              {...field}
              value={field.value || ""}
              label="Sexo"
              size="small"
              fullWidth
              error={!!errors.gender}
              helperText={errors.gender?.message}
              options={GENDER_OPTIONS}
            />
          )}
        />
        <Controller
          name="markingType"
          control={control}
          render={({ field }) => (
            <TextInput
              {...field}
              value={field.value || ""}
              label="Tipo de Marcação"
              textSize="sm"
              fullWidth
              error={!!errors.markingType}
              helperText={errors.markingType?.message}
            />
          )}
        />

        <div
          className={isMarkingDisabled ? "opacity-50 pointer-events-none" : ""}
        >
          <Controller
            name="marking"
            control={control}
            render={({ field }) => (
              <TextInput
                {...field}
                label="Marcação"
                textSize="sm"
                fullWidth
                disabled={isMarkingDisabled}
                error={!!errors.marking}
                helperText={errors.marking?.message}
                autoComplete="off"
              />
            )}
          />
        </div>

        <div className="md:col-span-2 mt-4">
          <Heading size="sm">Dados de Captura</Heading>
        </div>

        <div className="md:col-span-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-1 xs:grid-cols-2 gap-3">
                <Controller
                  name="latitude"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      label="Latitude"
                      type="number"
                      textSize="sm"
                      fullWidth
                      error={!!errors.latitude}
                      helperText={errors.latitude?.message}
                    />
                  )}
                />

                <Controller
                  name="longitude"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      label="Longitude"
                      type="number"
                      textSize="sm"
                      fullWidth
                      error={!!errors.longitude}
                      helperText={errors.longitude?.message}
                    />
                  )}
                />

                <Controller
                  name="ageEstimateYear"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      label="Idade (anos)"
                      type="number"
                      textSize="sm"
                      fullWidth
                      error={!!errors.ageEstimateYear}
                      helperText={errors.ageEstimateYear?.message}
                    />
                  )}
                />

                <Controller
                  name="weightKg"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      label="Peso (kg)"
                      type="number"
                      textSize="sm"
                      fullWidth
                      error={!!errors.weightKg}
                      helperText={errors.weightKg?.message}
                    />
                  )}
                />
              </div>

              <div className="flex-1">
                <Controller
                  name="observations"
                  control={control}
                  render={({ field }) => (
                    <TextInput
                      {...field}
                      label="Observações"
                      textSize="sm"
                      fullWidth
                      multiline
                      error={!!errors.observations}
                      helperText={errors.observations?.message}
                      className="min-h-20"
                    />
                  )}
                />
              </div>
            </div>

            <div className="h-48 md:h-70">
              {hasValidCoordinates ? (
                <Map.Root>
                  <Map.Localization latitude={lat} longitude={lng} draw />
                </Map.Root>
              ) : (
                <div className="flex flex-col items-center justify-center h-full text-gray-400 bg-slate-100 rounded-md shadow-sm gap-2 p-6 text-center">
                  <PublicIcon className="text-4xl" />
                  <Text size="sm">
                    Insira coordenadas válidas para visualizar o mapa.
                  </Text>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="md:col-span-2">
          <Controller
            name="attachments"
            control={control}
            render={({ field: { value, onChange, onBlur, name } }) => (
              <UploadInput
                value={value || []}
                onChange={onChange}
                onBlur={onBlur}
                name={name}
                multiple={config.uploadArchive.multiple}
                accept={config.uploadArchive.accept}
                maxFiles={config.uploadArchive.maxFiles}
              />
            )}
          />
          {errors.attachments && (
            <p className="text-red-500 text-sm mt-1">
              {errors.attachments.message}
            </p>
          )}
        </div>
      </form>
    </Modal>
  );
}

export default ModalNewIndividual;
